/**
 * The control-channel client: one socket, one cursor, exactly-once events.
 *
 * ## The contract it implements (docs/10 §2, §3.4, §10)
 *
 * 1. `HELLO` carries the server's `account_seq`. If the server is ahead of us,
 *    we send `RESUME { last_seq }` with **our own cursor** and the server replays
 *    `account_seq > last_seq`, then `REPLAY_DONE`. A fresh tab therefore rebuilds
 *    its positions from the journal instead of guessing them.
 * 2. `account_seq` is gapless and monotonic. `applyEvent` (state/projection)
 *    drops anything at or below the cursor, so a replay that overlaps live
 *    traffic is applied once. This client never de-duplicates *by itself* — the
 *    fold does — but it does detect a **gap** and asks for a replay rather than
 *    silently continuing with a hole in the book.
 * 3. Responses are correlated by `cl_ord_id` when the frame carries one. Several
 *    command responses (`ORDER_MODIFIED`, `ORDER_CANCELED`, …) do not, and
 *    commands for one account are answered in order, so those match the head of
 *    the pending queue. A response with no pending command is surfaced as an
 *    unsolicited notice, never dropped: it means the UI lost track, and hiding
 *    that is how a trader sees a fill the screen never mentioned.
 * 4. `PING`/`PONG` is the liveness check (the server sends `heartbeat_ms`). A
 *    socket that has been silent for two heartbeats is presumed dead and is
 *    closed locally — waiting for TCP to notice is how a desk stares at stale
 *    prices during a network partition.
 *
 * ## Numbers
 *
 * Quotes are decoded with `decodeQuoteFrame` from `@weke/protocol` — the
 * reference decoder, byte for byte — and immediately normalised to canonical
 * strings. The `Buffer` it expects is the `buffer` polyfill's, because the wire
 * format is defined in terms of it and re-writing the decoder for the browser
 * would be a second implementation of the one thing that must not diverge.
 */

import { Buffer } from 'buffer';

import { decodeQuoteFrame, parseServerMessage } from '@weke/protocol';
import type {
  AlertPayload,
  CommandPayload,
  HelloPayload,
  ReplayDonePayload,
  ResponsePayload,
  ServiceErrorPayload,
} from '@weke/protocol';

import { asWireEvent } from './events.js';
import type { WireEvent } from './events.js';
import type { Quote } from '../data/types.js';

export type ConnectionStatus = 'connecting' | 'live' | 'replaying' | 'offline';

export interface PendingCommand {
  readonly clOrdId: string;
  readonly sentAtMs: number;
  /** Which response kinds can legitimately answer this command. */
  readonly expects: readonly string[];
  readonly resolve: (response: ResponsePayload) => void;
  readonly fail: (error: ProtocolFailure) => void;
}

export interface ProtocolFailure {
  readonly code: string;
  readonly message: string;
  readonly clOrdId: string | null;
}

export interface ProtocolHandlers {
  readonly onHello: (payload: HelloPayload, receivedAtMs: number) => void;
  readonly onEvent: (event: WireEvent) => void;
  readonly onResponse: (payload: ResponsePayload) => void;
  readonly onAlert: (payload: AlertPayload) => void;
  readonly onError: (payload: ServiceErrorPayload) => void;
  readonly onReplayDone: (payload: ReplayDonePayload) => void;
  readonly onQuote: (quote: Quote) => void;
  readonly onStatus: (status: ConnectionStatus, detail: string | null) => void;
  readonly onPong: (rttMs: number) => void;
  readonly onGap: (cursor: string) => void;
  readonly onFatal: (failure: ProtocolFailure) => void;
}

export interface ProtocolClientOptions {
  readonly url: (token: string) => string;
  readonly token: () => string | null;
  /** The last applied `account_seq`, read at each connect. */
  readonly cursor: () => bigint;
  /** The symbols to (re)subscribe to after every connect. */
  readonly symbols: () => readonly string[];
  readonly handlers: ProtocolHandlers;
}

/** Response kinds a command may produce, by command kind. */
const EXPECTED_RESPONSES: Record<string, readonly string[]> = {
  PLACE_ORDER: ['ACCEPTED', 'REJECTED', 'REQUOTE'],
  CANCEL_ORDER: ['ORDER_CANCELED', 'REJECTED'],
  MODIFY_ORDER: ['ORDER_MODIFIED', 'REJECTED'],
  MODIFY_POSITION: ['POSITION_MODIFIED', 'REJECTED'],
  CLOSE_POSITION: ['POSITION_CLOSED', 'REJECTED'],
  CLOSE_BY: ['CLOSE_BY', 'REJECTED'],
  DEALER_DECISION: ['DEALER_ACCEPTED', 'REJECTED'],
};

const COMMAND_TIMEOUT_MS = 20_000;

/**
 * A v4 UUID for a command id.
 *
 * `crypto.randomUUID` is **secure-context only** (HTTPS or localhost). A
 * deployment served over plain HTTP on a LAN address therefore has no
 * `randomUUID`, and the old fallback returned `c-<base36>-<base36>` — which the
 * wire schema rejects, because a command `id` is validated as a UUID. The
 * symptom was an order rejected with `E_PROTOCOL` about a `uuid` pattern while
 * everything else streamed fine.
 *
 * `crypto.getRandomValues` *is* available in insecure contexts, so it is the
 * right source; `Math.random` remains only for a runtime with no WebCrypto at
 * all, where uniqueness per account is still enough.
 */
export function randomUuid(api: Crypto | undefined = globalThis.crypto): string {
  if (typeof api?.randomUUID === 'function') return api.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof api?.getRandomValues === 'function') api.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function randomId(): string {
  return randomUuid();
}

/** `cl_ord_id` must be ≤ 36 characters (doc 02 §1.1). */
export function newClOrdId(prefix = 't'): string {
  return `${prefix}-${randomId()}`.slice(0, 36);
}

export class ProtocolClient {
  private readonly options: ProtocolClientOptions;
  private socket: WebSocket | null = null;
  private pending: PendingCommand[] = [];
  private status: ConnectionStatus = 'offline';
  private heartbeatMs = 15_000;
  private lastMessageAtMs = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelayMs = 250;
  private closed = false;
  private awaitingHello = false;
  private lastPingSentAtMs = 0;
  private serverTime: string | null = null;
  /** The cursor sent with the most recent RESUME, for the status line. */
  private resumeFrom: string | null = null;

  constructor(options: ProtocolClientOptions) {
    this.options = options;
  }

  get connectionState(): ConnectionStatus {
    return this.status;
  }

  get serverClockSample(): string | null {
    return this.serverTime;
  }

  /** The cursor the last RESUME asked from; the status bar reports it. */
  get lastResumeFrom(): string | null {
    return this.resumeFrom;
  }

  connect(): void {
    if (this.closed) return;
    const token = this.options.token();
    if (token === null) {
      this.setStatus('offline', 'no session');
      return;
    }
    this.setStatus('connecting', null);
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.options.url(token));
    } catch (error) {
      this.scheduleReconnect(`could not open the socket: ${(error as Error).message}`);
      return;
    }
    socket.binaryType = 'arraybuffer';
    this.socket = socket;
    this.awaitingHello = true;

    socket.onopen = () => {
      this.lastMessageAtMs = Date.now();
      this.startHeartbeat();
    };
    socket.onmessage = (message: MessageEvent<ArrayBuffer | string>) => {
      this.lastMessageAtMs = Date.now();
      if (typeof message.data === 'string') this.onText(message.data);
      else this.onBinary(message.data);
    };
    socket.onerror = () => {
      // `onclose` always follows; the status is set there with a real code.
    };
    socket.onclose = (event: CloseEvent) => {
      this.stopHeartbeat();
      if (this.socket === socket) this.socket = null;
      if (this.closed) return;
      // 4401 is the edge refusing the token: a reconnect cannot fix that, and
      // the app must send the trader back to login (after one refresh attempt).
      if (event.code === 4401) {
        this.options.handlers.onFatal({
          code: 'E_AUTH',
          message: 'the session was refused',
          clOrdId: null,
        });
        this.setStatus('offline', 'session refused');
        return;
      }
      this.scheduleReconnect(event.reason.length > 0 ? event.reason : 'socket closed');
    };
  }

  /** Close and stop reconnecting (logout, or the tab is going away). */
  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const socket = this.socket;
    this.socket = null;
    if (socket !== null && socket.readyState <= WebSocket.OPEN) socket.close(1000, 'client closing');
    this.failAllPending({ code: 'E_UNAVAILABLE', message: 'the connection was closed', clOrdId: null });
  }

  /** Re-subscribe (the client remembers the symbol filter across reconnects). */
  subscribe(symbols: readonly string[]): void {
    this.send({
      type: 'SUBSCRIBE',
      payload: symbols.length === 0 ? { quotes: true } : { symbols: [...symbols], quotes: true },
    });
  }

  /**
   * Send a command and await its response. The caller keeps the `cl_ord_id` so a
   * retry after a dropped socket is idempotent (the engine refuses a duplicate
   * with a different payload and replays the original response for the same one).
   */
  command(payload: CommandPayload, clOrdId = newClOrdId()): Promise<ResponsePayload> {
    return new Promise<ResponsePayload>((resolve, fail) => {
      const pending: PendingCommand = {
        clOrdId,
        sentAtMs: Date.now(),
        expects: EXPECTED_RESPONSES[payload.kind] ?? ['ACCEPTED', 'REJECTED'],
        resolve,
        fail,
      };
      this.pending.push(pending);
      const sent = this.send({ type: 'COMMAND', payload, clOrdId });
      if (!sent) {
        this.pending = this.pending.filter((entry) => entry !== pending);
        fail({ code: 'E_UNAVAILABLE', message: 'not connected', clOrdId });
        return;
      }
      // A command that is never answered must not hang the ticket forever.
      setTimeout(() => {
        if (!this.pending.includes(pending)) return;
        this.pending = this.pending.filter((entry) => entry !== pending);
        fail({
          code: 'E_UNAVAILABLE',
          message: 'the server did not answer the command; it is safe to send it again',
          clOrdId,
        });
      }, COMMAND_TIMEOUT_MS);
    });
  }

  private send(frame: { type: string; payload: Record<string, unknown>; clOrdId?: string }): boolean {
    const socket = this.socket;
    if (socket === null || socket.readyState !== WebSocket.OPEN) return false;
    // The envelope's tenant and account must equal the session's. They are read
    // out of the signed access token (the server verifies the MAC and refuses a
    // mismatch), so this client cannot name an account it was not issued for.
    const claims = accountIdFromToken(this.options.token());
    if (claims === null) return false;
    socket.send(
      JSON.stringify({
        v: 1,
        kind: 'command',
        id: randomId(),
        ts: new Date().toISOString(),
        tenant_id: claims.tenantId,
        account_id: claims.accountId,
        seq: null,
        type: frame.type,
        payload: frame.payload,
        ...(frame.clOrdId === undefined ? {} : { cl_ord_id: frame.clOrdId }),
      }),
    );
    return true;
  }

  private setStatus(status: ConnectionStatus, detail: string | null): void {
    this.status = status;
    this.options.handlers.onStatus(status, detail);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.pingTimer = setInterval(() => {
      const now = Date.now();
      if (now - this.lastMessageAtMs > this.heartbeatMs * 2.5) {
        // Silence past two heartbeats: the socket is dead even if TCP has not
        // noticed. Close it locally and reconnect; the cursor makes it safe.
        this.socket?.close(4000, 'no traffic');
        return;
      }
      this.lastPingSentAtMs = now;
      this.send({ type: 'PING', payload: {} });
    }, Math.max(1_000, Math.floor(this.heartbeatMs / 2)));
  }

  private stopHeartbeat(): void {
    if (this.pingTimer !== null) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private scheduleReconnect(detail: string): void {
    if (this.closed || this.reconnectTimer !== null) return;
    this.setStatus('offline', detail);
    const delay = this.retryDelayMs;
    this.retryDelayMs = Math.min(5_000, Math.round(this.retryDelayMs * 2));
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, Math.round(delay * (0.7 + Math.random() * 0.6)));
  }

  private onBinary(data: ArrayBuffer): void {
    let quote;
    try {
      quote = decodeQuoteFrame(Buffer.from(data));
    } catch {
      // A malformed frame is the server's bug; dropping it is better than
      // showing bytes as a price. The next frame supersedes it anyway.
      return;
    }
    this.options.handlers.onQuote({
      symbol: quote.symbol,
      bid: quote.bid.toString(),
      ask: quote.ask.toString(),
      bidVolume: quote.bidVolume.toString(),
      askVolume: quote.askVolume.toString(),
      engineTsMs: quote.engineTsMs.toString(),
      recvTsMs: quote.recvTsMs.toString(),
      source: quote.source,
      sessionOpen: quote.sessionOpen,
      stale: quote.stale,
      seq: quote.seq.toString(),
      receivedAtMs: Date.now(),
    });
  }

  private onText(text: string): void {
    let frame;
    try {
      frame = parseServerMessage(JSON.parse(text));
    } catch (error) {
      this.options.handlers.onError({
        code: 'E_PROTOCOL',
        message: `unreadable server frame: ${(error as Error).message.slice(0, 200)}`,
        cl_ord_id: null,
      });
      return;
    }
    this.retryDelayMs = 250;
    switch (frame.type) {
      case 'HELLO':
        this.handleHello(frame.payload);
        return;
      case 'EVENT': {
        const event = asWireEvent(frame.payload);
        this.options.handlers.onEvent(event);
        if (this.status === 'replaying') this.setStatus('live', null);
        return;
      }
      case 'RESPONSE':
        this.settle(frame.payload);
        return;
      case 'REPLAY_DONE':
        this.options.handlers.onReplayDone(frame.payload);
        // The replay catches the book up; the subscription filter then goes back
        // on (a fresh trade sink starts with no filter, i.e. every symbol).
        this.subscribe(this.options.symbols());
        this.setStatus('live', null);
        return;
      case 'PONG':
        this.options.handlers.onPong(Date.now() - this.lastPingSentAtMs);
        return;
      case 'ALERT':
        this.options.handlers.onAlert(frame.payload);
        return;
      case 'ERROR':
        this.failPending(frame.payload);
        this.options.handlers.onError(frame.payload);
        return;
      default:
        return;
    }
  }

  private handleHello(payload: HelloPayload): void {
    this.heartbeatMs = payload.heartbeat_ms;
    this.serverTime = payload.server_time;
    this.startHeartbeat();
    this.options.handlers.onHello(payload, Date.now());
    const cursor = this.options.cursor();
    const serverSeq = BigInt(payload.account_seq);
    if (serverSeq > cursor) {
      // Our book is behind the server's journal: ask for the gap. The server
      // replays it and answers REPLAY_DONE; live events may arrive meanwhile and
      // are applied by the same cursor rule, so the overlap is free.
      this.setStatus('replaying', null);
      this.resumeFrom = cursor.toString();
      this.send({
        type: 'RESUME',
        payload: {
          last_seq: cursor.toString(),
          ...(this.options.symbols().length === 0
            ? {}
            : { symbols: [...this.options.symbols()] }),
        },
      });
    } else {
      this.setStatus('live', null);
      if (this.awaitingHello) this.subscribe(this.options.symbols());
    }
    this.awaitingHello = false;
  }

  /** Match one response to its command, in order, and resolve it. */
  private settle(payload: ResponsePayload): void {
    const clOrdId = 'clOrdId' in payload ? payload.clOrdId : null;
    let index = -1;
    if (clOrdId !== null) {
      index = this.pending.findIndex((entry) => entry.clOrdId === clOrdId);
    }
    if (index === -1) {
      // No clOrdId on the frame: commands are answered in order for one account.
      const head = this.pending.findIndex((entry) => entry.expects.includes(payload.kind));
      index = head;
    }
    if (index === -1) {
      this.options.handlers.onResponse(payload);
      return;
    }
    const [pending] = this.pending.splice(index, 1);
    this.options.handlers.onResponse(payload);
    pending?.resolve(payload);
  }

  private failPending(error: ServiceErrorPayload): void {
    const clOrdId = error.cl_ord_id;
    const index =
      clOrdId === null ? 0 : this.pending.findIndex((entry) => entry.clOrdId === clOrdId);
    if (index === -1 || this.pending.length === 0) return;
    const [pending] = this.pending.splice(index, 1);
    pending?.fail({ code: error.code, message: error.message, clOrdId: error.cl_ord_id });
  }

  private failAllPending(failure: ProtocolFailure): void {
    const entries = this.pending;
    this.pending = [];
    for (const entry of entries) entry.fail(failure);
  }

  /** Called by the app when a gap was detected and the cursor needs replaying. */
  requestResume(fromSeq: string): void {
    this.setStatus('replaying', null);
    this.resumeFrom = fromSeq;
    this.send({ type: 'RESUME', payload: { last_seq: fromSeq } });
  }
}

/**
 * The envelope's `tenant_id`/`account_id` must equal the session's.
 *
 * They are read out of the access token's payload (base64url, not verified here:
 * the server verifies the MAC and refuses a mismatch). Reading them beats
 * keeping a second copy of the session in this module, and a placeholder would
 * be refused by design.
 */
export function accountIdFromToken(token: string | null): { tenantId: string; accountId: string } | null {
  if (token === null) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1] ?? '';
    const padded = payload.replaceAll('-', '+').replaceAll('_', '/');
    const json = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
    const claims = JSON.parse(json) as { tid?: unknown; aid?: unknown };
    if (typeof claims.tid !== 'string' || typeof claims.aid !== 'string') return null;
    return { tenantId: claims.tid, accountId: claims.aid };
  } catch {
    return null;
  }
}
